import { queryClient } from "@/api/query-client"
import { postSessionMessage } from "./session-channel"
import { clearSessionHint } from "./session-hint"
import { stepUpStore } from "./step-up-store"
import { tokenStore } from "./token-store"

/** Why `/sign-in` was reached; the screen explains each (`auth:notice.*`). */
export type SignInReason = "reused" | "expired" | "banned" | "idle"

/** A refusal's problem code → the reason the sign-in screen gives (LLD §6.3). */
const REASON: Partial<Record<string, SignInReason>> = {
  refresh_reused: "reused",
  session_invalid: "expired",
  authentication_required: "expired",
  account_disabled: "banned",
}

/** The slice of a data router this module drives: the app's, or a test's. */
export interface SessionRouter {
  navigate(to: string, opts: { replace: boolean }): unknown
  state: { location: { pathname: string; search: string } }
}

let router: SessionRouter | null = null
let cache: { clear(): void } = queryClient

/** The app binds its router (App.tsx); returns the unbind. */
export function bindSessionRouter(r: SessionRouter): () => void {
  router = r
  return () => {
    if (router === r) router = null
  }
}

/** The query cache a sign-out empties: the provider's (SessionProvider binds it). */
export function bindSessionCache(c: { clear(): void }): () => void {
  cache = c
  return () => {
    if (cache === c) cache = queryClient
  }
}

export interface SessionLost {
  /** Whether this tab held a token: without one, a failed check is not an event. */
  hadToken: boolean
  /** The problem code that ended it, mapped to a `reason=`. */
  code?: string
  /** A reason not carried by a problem code (the idle sign-out). */
  reason?: SignInReason
  /** The user asked to leave: no `from=` to come back to. */
  signedOut?: boolean
  /** False when following another tab's sign-out, so it is not echoed. */
  broadcast?: boolean
}

/**
 * The one disruptive exit (LLD §6.3, akav-eli's onSessionLost(hadToken)).
 * The store and the boot hint are always cleared. With `hadToken` false that
 * is all: an anonymous visitor's failed cookie check is the ordinary case,
 * and clearing the cache under the sign-in screen's own queries would make
 * them refetch, fail and clear again, forever.
 */
export function onSessionLost(o: SessionLost): void {
  tokenStore.clear()
  stepUpStore.clear()
  clearSessionHint()
  if (!o.hadToken) return
  cache.clear()
  const reason = o.reason ?? (o.code ? REASON[o.code] : undefined)
  if (o.broadcast !== false) postSessionMessage({ kind: "signout", ...(reason && { reason }) })
  navigate(signInUrl(o, reason))
}

function signInUrl(o: SessionLost, reason: SignInReason | undefined): string {
  const here = router?.state.location ?? window.location
  const params = new URLSearchParams()
  // Coming back to sign-in itself (or after asking to leave) needs no `from=`.
  if (!o.signedOut && !here.pathname.startsWith("/sign-in")) {
    params.set("from", here.pathname + here.search)
  }
  if (reason) params.set("reason", reason)
  const query = params.toString()
  return query ? `/sign-in?${query}` : "/sign-in"
}

function navigate(to: string): void {
  if (router) void router.navigate(to, { replace: true })
  else window.location.replace(to)
}
